import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, BellOff } from "lucide-react";
import { useSeriesInterest } from "@/hooks/useSeriesInterest";
import { useToast } from "@/hooks/use-toast";

type SectionLite = {
  id: string;
  title: string;
  subtitle: string | null;
  icon: string | null;
};

const SeriesInterestsCard = () => {
  const { interests, unregister, isLoading } = useSeriesInterest();
  const { toast } = useToast();

  const sectionIds = interests.map((i) => i.section_id);

  const { data: sections } = useQuery({
    queryKey: ["series-interest", "sections", sectionIds.sort().join(",")],
    queryFn: async (): Promise<SectionLite[]> => {
      if (sectionIds.length === 0) return [];
      const { data, error } = await supabase
        .from("experience_sections")
        .select("id, title, subtitle, icon")
        .in("id", sectionIds);
      if (error) throw error;
      return (data ?? []) as SectionLite[];
    },
    enabled: sectionIds.length > 0,
  });

  const handleUnsubscribe = async (sectionId: string, title: string) => {
    try {
      await unregister.mutateAsync(sectionId);
      toast({
        title: "Notifications turned off",
        description: `Removed ${title} from your interests.`,
      });
    } catch (e: any) {
      toast({
        title: "Something went wrong",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card id="interests">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          <div>
            <CardTitle className="text-lg">Series I follow</CardTitle>
            <CardDescription>Get notified when new events launch</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : interests.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            <BellOff className="h-4 w-4 inline-block mr-1 opacity-60" />
            You're not following any series yet. Visit the events page and tap "Notify me" on a series.
          </div>
        ) : (
          <ul className="space-y-2">
            {(sections ?? []).map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between gap-2 rounded-md border p-3"
              >
                <div className="min-w-0">
                  <div className="font-medium text-foreground truncate">
                    {s.icon ? `${s.icon} ` : ""}
                    {s.title}
                  </div>
                  {s.subtitle && (
                    <div className="text-xs text-muted-foreground truncate">{s.subtitle}</div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleUnsubscribe(s.id, s.title)}
                  disabled={unregister.isPending}
                >
                  Unfollow
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default SeriesInterestsCard;
